export interface AudioRecorderResult {
	blob: Blob;
	buffer: Float32Array[]; // one Float32Array per channel
}

export class AudioRecorder {
	private ctx: AudioContext;
	private source: MediaStreamAudioSourceNode | null = null;
	private processor: ScriptProcessorNode | null = null;
	private recording = false;
	private channelChunks: Float32Array[][] = [];
	private channels = 1;
	private bufferSize: number;

	constructor(ctx: AudioContext, config?: { bufferSize?: number }) {
		this.ctx = ctx;
		this.bufferSize = config?.bufferSize ?? 4096;
	}

	async init(stream: MediaStream): Promise<void> {
		this.teardown();
		this.source = this.ctx.createMediaStreamSource(stream);

		// Use provided channel count if available; default to mono.
		this.channels = Math.max(1, this.source.channelCount || 1);

		// Deprecated but supported; simple, low-overhead capture.
		// Inputs/outputs match channel count; we won't write to output.
		this.processor = this.ctx.createScriptProcessor(this.bufferSize, this.channels, this.channels);
		this.channelChunks = Array.from({ length: this.channels }, () => []);

		this.processor.onaudioprocess = (e) => {
			if (!this.recording) return;
			const input = e.inputBuffer;
			const chCount = Math.min(this.channels, input.numberOfChannels || 1);
			for (let ch = 0; ch < chCount; ch++) {
				const src = input.getChannelData(ch);
				// Copy because the buffer memory may be reused
				this.channelChunks[ch].push(new Float32Array(src));
			}
		};

		this.source.connect(this.processor);
		// Some browsers require an output connection to process callbacks.
		try {
			this.processor.connect(this.ctx.destination);
		} catch {
			// ignore if connecting to destination is disallowed (e.g., autoplay policies)
		}
	}

	async start(): Promise<void> {
		this.recording = true;
	}

	async stop(): Promise<AudioRecorderResult> {
		this.recording = false;

		const buffers: Float32Array[] = this.channelChunks
			.map((chunks) => {
				let total = 0;
				for (const c of chunks) total += c.length;
				const out = new Float32Array(total);
				let offset = 0;
				for (const c of chunks) {
					out.set(c, offset);
					offset += c.length;
				}
				return out;
			})
			.filter((b) => b.length > 0); // drop empty channels to avoid zero-length mono mix

		this.teardown();

		// Component re-encodes to WAV from buffers; blob is placeholder.
		return { blob: new Blob([], { type: 'application/octet-stream' }), buffer: buffers };
	}

	private teardown() {
		try {
			if (this.processor) {
				this.processor.disconnect();
				this.processor.onaudioprocess = null;
			}
		} catch {}
		try {
			this.source?.disconnect();
		} catch {}
		this.processor = null;
		this.source = null;
		this.channelChunks = [];
	}
}

// --- Audio manipulation helpers (shared) ---
// Mix down to mono if needed
export const toMono = (buffers: Float32Array[]): Float32Array => {
	if (!buffers || buffers.length === 0) return new Float32Array(0);

	// Prefer non-empty channels only
	const nonEmpty = buffers.filter((b) => b && b.length > 0);
	if (nonEmpty.length === 0) return new Float32Array(0);
	if (nonEmpty.length === 1) return nonEmpty[0];

	const left = nonEmpty[0];
	const right = nonEmpty[1];
	const maxLen = Math.max(left.length, right.length);
	const mono = new Float32Array(maxLen);
	for (let i = 0; i < maxLen; i++) {
		const l = i < left.length ? left[i] : 0;
		const r = i < right.length ? right[i] : 0;
		const c = (i < left.length ? 1 : 0) + (i < right.length ? 1 : 0);
		mono[i] = c > 0 ? (l + r) / c : 0; // average only present channels
	}
	return mono;
};

// Downsample float32 PCM to target sample rate using averaging to reduce aliasing
export const downsampleBuffer = (buffer: Float32Array, sampleRate: number, outRate: number) => {
	if (!buffer || sampleRate === outRate) {
		return buffer;
	}
	const ratio = sampleRate / outRate;
	const newLen = Math.max(1, Math.round(buffer.length / ratio));
	const result = new Float32Array(newLen);
	let offsetResult = 0;
	let offsetBuffer = 0;
	while (offsetResult < result.length) {
		const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
		let accum = 0;
		let count = 0;
		for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
			accum += buffer[i];
			count++;
		}
		result[offsetResult] = count ? accum / count : 0;
		offsetResult++;
		offsetBuffer = nextOffsetBuffer;
	}
	return result;
};

// Encode PCM Float32 mono into WAV (PCM 16-bit, mono)
export const encodeWAV = (samples: Float32Array, sampleRate: number) => {
	const bytesPerSample = 2;
	const blockAlign = 1 * bytesPerSample; // mono
	const buffer = new ArrayBuffer(44 + samples.length * bytesPerSample);
	const view = new DataView(buffer);

	const writeString = (offset: number, str: string) => {
		for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
	};

	// RIFF header
	writeString(0, 'RIFF');
	view.setUint32(4, 36 + samples.length * bytesPerSample, true);
	writeString(8, 'WAVE');

	// fmt chunk
	writeString(12, 'fmt ');
	view.setUint32(16, 16, true); // PCM chunk size
	view.setUint16(20, 1, true); // audio format = PCM
	view.setUint16(22, 1, true); // channels = 1 (mono)
	view.setUint32(24, sampleRate, true); // sample rate
	view.setUint32(28, sampleRate * blockAlign, true); // byte rate
	view.setUint16(32, blockAlign, true); // block align
	view.setUint16(34, 16, true); // bits per sample

	// data chunk
	writeString(36, 'data');
	view.setUint32(40, samples.length * bytesPerSample, true);

	// Write PCM samples
	let offset = 44;
	for (let i = 0; i < samples.length; i++, offset += 2) {
		// clamp
		let s = Math.max(-1, Math.min(1, samples[i]));
		// convert to 16-bit PCM
		view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
	}

	return new Blob([view], { type: 'audio/wav' });
};

// Determine if a mono PCM buffer is effectively silent / inaudible.
// Returns true if we should discard (empty or peak amplitude below threshold).
export const isInaudible = (mono: Float32Array, peakThreshold = 0.02): boolean => {
	if (!mono.length) {
		return true; // empty buffer
	}
	let peak = 0;
	for (let i = 0; i < mono.length; i++) {
		const a = Math.abs(mono[i]);
		if (a > peak) {
			peak = a;
			if (peak >= peakThreshold) {
				return false; // early exit once above threshold
			}
		}
	}
	return peak < peakThreshold;
};

// High-level helper: build a WAV File from a recorder result.
export interface BuildWavOptions {
	inputSampleRate: number; // sampleRate of the AudioContext used to capture
	targetSampleRate?: number; // default 16000 Hz
	peakThreshold?: number; // default 0.02
	fileNamePrefix?: string; // default 'voice'
	// Speech detection options
	vadRmsThreshold?: number; // default 0.02
	vadMinActiveMs?: number; // default 300 ms
	vadMinActiveRatio?: number; // default 0.1 (10%)
}

export const buildWavFileFromResult = async (
	result: AudioRecorderResult,
	options: BuildWavOptions
): Promise<File | null> => {
	const targetRate = options.targetSampleRate ?? 16000;
	const threshold = options.peakThreshold ?? 0.02;
	const prefix = options.fileNamePrefix ?? 'voice';
	const inputRate = options.inputSampleRate || 44100;

	const mono = toMono(result?.buffer || []);
	// Quick reject: peak inaudible
	if (isInaudible(mono, threshold)) {
		return null;
	}
	// Stronger speech presence check using RMS over frames
	const hasSpeech = hasDiscernibleSpeech(mono, inputRate, {
		rmsThreshold: options.vadRmsThreshold ?? 0.02,
		minActiveMs: options.vadMinActiveMs ?? 300,
		minActiveRatio: options.vadMinActiveRatio ?? 0.1,
	});
	if (!hasSpeech) {
		return null;
	}
	const down = downsampleBuffer(mono, inputRate, targetRate);
	const wavBlob = encodeWAV(down, targetRate);
	const file = new File([wavBlob], `${prefix}-${Date.now()}.wav`, { type: 'audio/wav' });
	return file;
};

// VAD (voice activity detection) based on RMS energy per frame ---
export interface VadOptions {
	rmsThreshold: number; // Energy threshold for a frame to be considered active (0..1)
	minActiveMs: number; // Minimum accumulated active ms to accept speech
	minActiveRatio: number; // Minimum fraction of frames active
	frameMs?: number; // default 20ms frames
}

export const hasDiscernibleSpeech = (
	samples: Float32Array,
	sampleRate: number,
	opts?: Partial<VadOptions>
): boolean => {
	if (!samples.length) return false;

	const frameMs = opts?.frameMs ?? 20;
	const frameLen = Math.max(1, Math.round((sampleRate * frameMs) / 1000));
	const rmsThreshold = opts?.rmsThreshold ?? 0.02;
	const minActiveMs = opts?.minActiveMs ?? 300;
	const minActiveRatio = opts?.minActiveRatio ?? 0.1;

	let activeFrames = 0;
	let totalFrames = 0;
	for (let i = 0; i < samples.length; i += frameLen) {
		const end = Math.min(samples.length, i + frameLen);
		let sumSquares = 0;
		const len = end - i;
		if (len <= 0) break;
		for (let j = i; j < end; j++) {
			const v = samples[j];
			sumSquares += v * v;
		}
		const rms = Math.sqrt(sumSquares / len);
		if (rms >= rmsThreshold) activeFrames++;
		totalFrames++;
	}
	if (totalFrames === 0) return false;

	const activeRatio = activeFrames / totalFrames;
	const activeMs = activeFrames * frameMs;
	return activeMs >= minActiveMs && activeRatio >= minActiveRatio;
};
